import { type ReactNode, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import type { Utskicksprojekt } from '../types.ts';
import useDataSet from '../../../hooks/useDataSet.ts';
import usePDL from '../../../pdl/usePDL.ts';
import { getMessageTitle } from '../utils.ts';
import { NotInProject } from './NotInProject.tsx';
import { ProjectHeader } from './ProjectHeader.tsx';
import { getNotificationMethodText, PatientPortal } from './PatientPortal.tsx';
import { LetterLink } from './LetterLink.tsx';
import { NotificationHistory } from './NotificationHistory.tsx';

export const ProjectViewer = (props: {
    utskicksprojekt: Utskicksprojekt;
    sparrad: boolean;
    reload: () => void;
}) => {
    const { utskicksprojekt, reload, sparrad } = props;
    const [notificationContent, setNotificationContent] = useState<ReactNode>(null);
    const isInUtskicksprojekt = !!utskicksprojekt.svarRootTableId;
    const ds = useDataSet();
    const pdl = usePDL();
    const hasPatientPortal = !!ds.Patientportal;

    const onLetterCreated = () => {
        setNotificationContent('Följebrev skapat.');
    };

    const onNotified = () => {
        setNotificationContent((
            <div className="d-flex align-items-center gap-1">
                <FontAwesomeIcon icon={faCircleCheck} className="color-success" />
                <div>Notifiering skickad via: {pdl.patientPortal.activeNotificationMethods.map(x => getNotificationMethodText(x.textValue)).join(', ')}</div>
            </div>
        ));
    };

    return (
        <tr className="align-middle">
            <td>
                <ProjectHeader utskicksprojekt={utskicksprojekt} sparrad={sparrad} />
            </td>
            <td>
                {utskicksprojekt.personalCode}
                {!isInUtskicksprojekt && <NotInProject utskicksprojekt={utskicksprojekt} reload={reload} sparrad={sparrad} />}
            </td>
            <td>{isInUtskicksprojekt && utskicksprojekt.answers.length}</td>
            <td>
                {utskicksprojekt.handledByIpo && !!utskicksprojekt.personalCode && !!utskicksprojekt.svarRootTableId && !sparrad && process.env.RCC_ENV !== 'external_demo' && (
                    <LetterLink
                        personalCode={utskicksprojekt.personalCode}
                        displayName={getMessageTitle(utskicksprojekt)}
                        svarRootTableId={utskicksprojekt.svarRootTableId}
                        deltagare={utskicksprojekt.deltagare}
                        onLetterCreated={onLetterCreated}
                    />
                )}
            </td>
            <td>
                <div className="flex-grow-1">
                    {process.env.RCC_ENV !== 'external_demo' && (
                        <div>
                            {!notificationContent && isInUtskicksprojekt && hasPatientPortal && !sparrad && utskicksprojekt.handledByIpo && (
                                <PatientPortal
                                    svarRootTableId={utskicksprojekt.svarRootTableId}
                                    deltagare={utskicksprojekt.deltagare}
                                    onNotified={onNotified}
                                />
                            )}
                        </div>
                    )}

                    {!!notificationContent && (
                        <div>{notificationContent}</div>
                    )}
                </div>
            </td>
            <td>
                {process.env.RCC_ENV !== 'external_demo' && !!utskicksprojekt.deltagare && (
                    <NotificationHistory rrId={utskicksprojekt.deltagare.data.REGISTERRECORD_ID} />
                )}
            </td>
        </tr>
    );
};
